import mixpanel from "mixpanel-browser";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCountryCategoryEvents, getSingleEvent } from "../../services/data/events";
import { selectCategories } from "../../store/slice/categories";
import { selectCountries } from "../../store/slice/countries";
import { getEvents, selectEventsData } from "../../store/slice/events";
import { selectUser } from "../../store/slice/user";
import { updateLoader } from "../../store/slice/loading";
import { selectLoading } from "../../store/slice/loading";
import { convertDate, currencyJson, getLeastPrice } from "../../lib/helper";
import debounce from "../../lib/debounce";
import { useTranslation } from "react-i18next";
import BuyTicket from "../../components/Modals/BuyTicket";
import SeatingMap from "../../components/Modals/SeatingMap";
import { MobileNumberModal } from "../../components";
import "./newDesignHome.css";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
);
const DateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
);
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
);
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 20.5V3.5c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z" /></svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>
);
const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
);
const FEATURE_ICONS = {
  instantTickets: <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6z" />,
  secureCheckout: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  mobileFirst: <><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></>,
  bestPrices: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><path d="M7 7h.01" /></>,
};

const priceOf = (t, language, data) => {
  const amount = getLeastPrice(data.tickets) ?? data?.start_price?.price;
  const currency = data?.tickets?.[0]?.currency || data?.start_price?.currency;
  if (data?.start_price?.is_free) return t("event_detail_page.free");
  return `${amount} ${currencyJson?.[currency]?.[language] || ""}`.trim();
};

const EventCard = ({ data, language, t, onGetTickets }) => {
  const navigate = useNavigate();
  const { day, month } = convertDate(data.event_date, t);
  return (
    <div className="event-card" onClick={() => navigate(`/event-details/${data.slug}`)}>
      <div className="event-image" style={{ backgroundImage: data?.full_image ? `url(${data.full_image})` : undefined }} />
      <div className="event-details">
        <div className="event-date">{day} {month}</div>
        <div className="event-title">{data?.translation?.[language]?.name}</div>
        <div className="event-location"><PinIcon />{data?.translation?.[language]?.address}</div>
        <div className="event-footer">
          <div className="event-price">{priceOf(t, language, data)}</div>
          <button
            className="event-cta"
            onClick={(e) => {
              e.stopPropagation();
              onGetTickets(data);
            }}
          >
            {t("marketing.home.getTickets")}
          </button>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const categories = useSelector(selectCategories);
  const countries = useSelector(selectCountries);
  const { language, countryId, category } = useSelector(selectUser);
  const { searchKey, data: searchResults } = useSelector(selectEventsData);
  const { loader } = useSelector(selectLoading);
  const isArabic = language === "ar";

  const realCategories = categories.filter((c) => c?.id !== 0);
  const [categoryEvents, setCategoryEvents] = useState({});
  const [catLoading, setCatLoading] = useState(true);
  const [showingAllRegions, setShowingAllRegions] = useState(false);
  const trackRefs = useRef({});
  const [searchInput, setSearchInput] = useState(searchKey || "");
  const [slideIdx, setSlideIdx] = useState(0);

  // Quick-buy: "Get Tickets" opens the ticket modal directly from the
  // homepage card (matching new-design), instead of requiring a trip to
  // the event details page first. Reuses the exact same BuyTicket/
  // SeatingMap/login flow as EventDetails' buyTikcet() — same login gate,
  // same seated-vs-GA branching, same checkout — just triggered from here.
  const [quickBuyData, setQuickBuyData] = useState(null);
  const [quickBuyLoading, setQuickBuyLoading] = useState(false);
  const [buyTicketModal, setBuyTicketModal] = useState(false);
  const [seatingMapModal, setSeatingMapModal] = useState(false);
  const [loginModal, setLoginModal] = useState(false);
  const carouselTimer = useRef(null);

  const activeCountry = countries.find((c) => c?.id === countryId);
  const region =
    (!showingAllRegions && activeCountry?.translation?.[language]?.name) || t("marketing.nav.everywhere");

  const openVendorLink = () => {
    window.open("https://admin.o7events.com/vendor", "_blank", "noopener,noreferrer");
  };

  const { token } = user;
  const handleGetTickets = async (ev) => {
    if (!token) {
      setLoginModal(true);
      return;
    }
    setQuickBuyLoading(true);
    try {
      const res = await getSingleEvent(ev.slug, token);
      const fullEvent = res?.data?.data;
      setQuickBuyData(fullEvent);
      if (fullEvent?.ticket_type === "seating") {
        setSeatingMapModal(true);
      } else {
        setBuyTicketModal(true);
      }
    } catch (err) {
      // fall back to the full event page if the quick-fetch fails
      window.location.assign(`/event-details/${ev.slug}`);
    } finally {
      setQuickBuyLoading(false);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-o7-home", "1");
    return () => document.documentElement.removeAttribute("data-o7-home");
  }, []);

  useEffect(() => {
    mixpanel?.track("home", { email: user?.email, name: user?.name || "anonymous" });
  }, []);

  useEffect(() => {
    if (!realCategories.length) return;
    let cancelled = false;
    setCatLoading(true);
    const fetchRows = (country) =>
      Promise.all(
        realCategories.map((cat) =>
          getCountryCategoryEvents({ page: 1, number: 12, country, category: cat.id })
            .then((res) => ({ id: cat.id, events: (res?.data?.data?.data || []).filter((e) => e?.status) }))
            .catch(() => ({ id: cat.id, events: [] }))
        )
      );
    (async () => {
      let results = await fetchRows(countryId);
      let all = false;
      // The user's region has no events — fall back to showing events from
      // every region instead of an empty homepage.
      if (countryId && results.every((r) => !r.events.length)) {
        results = await fetchRows(null);
        all = true;
      }
      if (cancelled) return;
      const map = {};
      results.forEach((r) => { map[r.id] = r.events; });
      setCategoryEvents(map);
      setShowingAllRegions(all);
      setCatLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId, categories.length]);

  const featuredEvents = useMemo(() => {
    const all = realCategories.flatMap((cat) =>
      (categoryEvents[cat.id] || []).map((ev) => ({ ...ev, __cat: cat }))
    );
    return all.slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryEvents]);

  useEffect(() => {
    if (slideIdx >= featuredEvents.length) setSlideIdx(0);
  }, [featuredEvents, slideIdx]);

  useEffect(() => {
    if (featuredEvents.length < 2) return;
    carouselTimer.current = setInterval(() => {
      setSlideIdx((i) => (i + 1) % featuredEvents.length);
    }, 6000);
    return () => clearInterval(carouselTimer.current);
  }, [featuredEvents.length]);

  const handleSearchResult = async (text) => {
    const res = await getCountryCategoryEvents({
      searchKey: text,
      page: 1,
      number: 12,
      country: showingAllRegions ? null : countryId,
      category: category?.id,
    });
    dispatch(getEvents({ ...res?.data?.data, searchKey: text }));
    dispatch(updateLoader({ loader: false }));
  };
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    dispatch(updateLoader({ loader: true }));
    debounce(() => handleSearchResult(v), 1500);
  };

  const scrollRow = (catId, dir) => {
    const el = trackRefs.current[catId];
    if (!el) return;
    const amount = 340 * (dir === "prev" ? -1 : 1) * (isArabic ? -1 : 1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const goSlide = (dir) => {
    if (!featuredEvents.length) return;
    setSlideIdx((i) => {
      const n = featuredEvents.length;
      return dir === "prev" ? (i - 1 + n) % n : (i + 1) % n;
    });
  };

  return (
    <div className="o7-home" dir={isArabic ? "rtl" : "ltr"}>
      {/* Featured carousel */}
      <div className="hero-carousel">
        <div className="carousel-viewport">
          {featuredEvents.map((ev, i) => {
            const { day, month } = convertDate(ev.event_date, t);
            return (
              <div
                key={ev.id || i}
                className={`carousel-slide${i === slideIdx ? " active" : ""}`}
                style={{ backgroundImage: ev?.full_image ? `url(${ev.full_image})` : undefined }}
              >
                <div className="carousel-content">
                  <span className="carousel-badge">{ev.__cat?.translation?.[language]?.name}</span>
                  <h2>{ev?.translation?.[language]?.name}</h2>
                  <div className="carousel-meta">{day} {month} &middot; {ev?.translation?.[language]?.address}</div>
                  <div className="carousel-actions">
                    <span className="carousel-price">{priceOf(t, language, ev)}</span>
                    <button className="carousel-cta" onClick={() => handleGetTickets(ev)}>
                      {t("marketing.home.getTickets")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {!catLoading && featuredEvents.length === 0 && (
            <div className="carousel-empty">
              <div className="carousel-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6z" />
                  <path d="M13 5v2M13 17v2M13 11v2" />
                </svg>
              </div>
              <h2>{t("marketing.home.noEventsTitle")}</h2>
              <p>{t("home_page.no_events")}</p>
            </div>
          )}
          {featuredEvents.length > 1 && (
            <>
              <button className="carousel-arrow carousel-prev" onClick={() => goSlide("prev")} aria-label="Previous"><ChevronLeft /></button>
              <button className="carousel-arrow carousel-next" onClick={() => goSlide("next")} aria-label="Next"><ChevronRight /></button>
              <div className="carousel-dots">
                {featuredEvents.map((_, i) => (
                  <button key={i} className={`carousel-dot${i === slideIdx ? " active" : ""}`} onClick={() => setSlideIdx(i)} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Search + dates */}
      <div className="search-row">
        <div className="big-search">
          <SearchIcon />
          <input type="text" placeholder={t("navbar.input")} value={searchInput} onChange={handleSearchChange} />
        </div>
        <button className="date-btn"><DateIcon />{t("marketing.home.chooseDates")}</button>
      </div>

      {/* Hero tagline */}
      <div className="hero">
        <div className="hero-content">
          <h1>
            {t("marketing.home.heroLine1")}<br />{t("marketing.home.heroLine2")}<br />{t("marketing.home.heroLine3")}
          </h1>
          <p className="hero-subtitle">{t("marketing.home.heroSubtitle", { region })}</p>
          <div className="app-download">
            <a href="https://apps.apple.com/gb/app/o7-events/id6757104108" target="_blank" rel="noopener noreferrer" className="store-badge">
              <span className="store-icon"><AppleIcon /></span>
              <span className="store-text">
                <span className="store-label">{t("marketing.home.downloadOnThe")}</span>
                <span className="store-name">App Store</span>
              </span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.o7events.app" target="_blank" rel="noopener noreferrer" className="store-badge">
              <span className="store-icon"><PlayIcon /></span>
              <span className="store-text">
                <span className="store-label">{t("marketing.home.getItOn")}</span>
                <span className="store-name">Google Play</span>
              </span>
            </a>
          </div>
        </div>
        <div className="hero-ticket" aria-hidden="true">
          <div className="ticket-main">
            <div className="ticket-label">O7 EVENTS</div>
            <div className="ticket-title">{t("marketing.ticketFlow.admitOne")}</div>
            <div className="ticket-meta">ROW G &middot; SEAT 12</div>
          </div>
          <div className="ticket-stub"><div className="ticket-barcode" /></div>
        </div>
      </div>

      {/* Category rows (real, dynamic) — skipped entirely when there's
          nothing to show, since the carousel above already communicates
          the empty state; avoids an awkward duplicate blank section. */}
      {(searchKey || catLoading || realCategories.some((cat) => (categoryEvents[cat.id] || []).length > 0)) && (
      <div className="featured-section" id="events">
        <div className="events-rows">
          {searchKey && !loader ? (
            /* search results: flat single row-per-nothing fallback, reuse event-card */
            <div className="cat-row">
              <div className="cat-row-head"><h3>{t("marketing.nav.home")}</h3></div>
              <div className="cat-row-track">
                {(searchResults || []).map((ev, i) => (
                  <EventCard key={i} data={ev} language={language} t={t} onGetTickets={handleGetTickets} />
                ))}
              </div>
            </div>
          ) : (
            realCategories.map((cat) => {
              const evs = categoryEvents[cat.id] || [];
              if (!catLoading && !evs.length) return null;
              return (
                <div className="cat-row" key={cat.id}>
                  <div className="cat-row-head">
                    <h3>{cat?.translation?.[language]?.name}</h3>
                    <div className="cat-row-arrows">
                      <button className="cat-arrow" onClick={() => scrollRow(cat.id, "prev")} aria-label="Scroll left"><ChevronLeft /></button>
                      <button className="cat-arrow" onClick={() => scrollRow(cat.id, "next")} aria-label="Scroll right"><ChevronRight /></button>
                    </div>
                  </div>
                  <div className="cat-row-track" ref={(el) => (trackRefs.current[cat.id] = el)}>
                    {catLoading
                      ? Array(3).fill(0).map((_, i) => <div key={i} className="event-card" style={{ flex: "0 0 330px", height: "412px", opacity: 0.4 }} />)
                      : evs.map((ev, i) => <EventCard key={i} data={ev} language={language} t={t} onGetTickets={handleGetTickets} />)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      )}

      {/* Why choose O7 (static) */}
      <div className="why-section">
        <div className="why-content">
          <div className="why-text">
            <h2>{t("marketing.home.whyChoose")}</h2>
            <p>{t("marketing.home.whyChooseBody")}</p>
          </div>
          <div className="features-grid">
            {Object.keys(FEATURE_ICONS).map((key) => (
              <div className="feature" key={key}>
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">{FEATURE_ICONS[key]}</svg>
                </div>
                <h3>{t(`marketing.home.${key}`)}</h3>
                <p>{t(`marketing.home.${key}Body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Built for Organizers (static) */}
      <div className="organizers-section">
        <div className="organizers-content">
          <h2>{t("marketing.home.builtForOrganizers")}</h2>
          <p>{t("marketing.home.builtForOrganizersBody")}</p>
          <button className="btn-create" onClick={openVendorLink}>{t("marketing.home.createYourEvent")}</button>
          <div className="organizer-benefits">
            {["easySetup", "realTimeAnalytics", "builtInMarketing"].map((key, i) => (
              <div className="benefit" key={key}>
                <div className="benefit-number">{String(i + 1).padStart(2, "0")}</div>
                <h3>{t(`marketing.home.${key}`)}</h3>
                <p>{t(`marketing.home.${key}Body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {quickBuyLoading && (
        <div className="quick-buy-loading">
          <div className="quick-buy-spinner" />
        </div>
      )}
      {quickBuyData && (
        <>
          <BuyTicket open={buyTicketModal} setOpen={setBuyTicketModal} data={quickBuyData} />
          <SeatingMap open={seatingMapModal} setOpen={setSeatingMapModal} data={quickBuyData} />
        </>
      )}
      <MobileNumberModal open={loginModal} setOpen={setLoginModal} />
    </div>
  );
};

export default Home;
